import { ReactNode } from 'react'

export enum ReportsEnum {
  timeReport = 'timeReport',
  duration = 'duration',
  instruments = 'instruments',
  psychology = 'psychology',
  tradingStrategy = 'tradingStrategy',
  tradingConfluence = 'tradingConfluence',
  riskManagement = 'riskManagement',
  drawdown = 'drawdown',
}

export type WidgetHeaderType = {
  text: string
  explanation?: string | null
  children?: ReactNode
}

export type IndicatorsPropsType = {
  positiveLabel: string
  negativeLabel: string
  middleLabel?: string
}

export type CircularBarProps = {
  title: string
  positiveVal: number
  negativeVal: number
  positiveLabel: string
  negativeLabel: string
  positiveTextComponent?: ReactNode
  negativeTextComponent?: ReactNode
}

export type ReportDto = {
  account: number
  period: (string | null)[]
}

export type PositiveVsNegativeType = {
  moneyWise: {
    negative: number
    positive: number
  }
  numberWise: {
    negative: number
    positive: number
  }
}

export type ChartOfCommentsItem = {
  is_positive: boolean
  count: number
  name: string
}

export type ChartOfCommentsType = {
  negative: ChartOfCommentsItem[]
  positive: ChartOfCommentsItem[]
  isPositive: boolean
}

export type StressPerformanceType = {
  negative: {
    loss: number
    win: number
  }
  positive: {
    loss: number
    win: number
  }
}

export type TopInstrumentByLevelOfStressType = {
  symbol: string
  perCent: number
}

export type StressDistributionType = {
  time: string
  totalTrades: number
  negativePerCent: number
  positivePerCent: number
}

export type MetaType = {
  account: number
  period: [string | null, string | null]
}

export type PsychologyType = {
  positiveVsNegative: PositiveVsNegativeType
  chartOfComments: ChartOfCommentsType
  stressPerformance: StressPerformanceType
  topInstrumentByLevelOfStress: TopInstrumentByLevelOfStressType[]
  stressDistribution: StressDistributionType[]
  emotionPerformance: {
    tag: string
    isNegative: boolean
    emotionGroup: 'risk' | 'context' | 'regulating'
    trades: number
    winRate: number
    avgPnl: number
    pnl: number
  }[]
  planAdherencePerformance: {
    followedPlan: {
      trades: number
      winRate: number
      avgPnl: number
      pnl: number
    }
    didNotFollowPlan: {
      trades: number
      winRate: number
      avgPnl: number
      pnl: number
    }
  }
  ruleBreakdown: {
    rule: string
    trades: number
    lossRate: number
    avgPnl: number
    pnl: number
  }[]
  sentimentCorrelation: {
    bucket: string
    trades: number
    winRate: number
    avgPnl: number
    sentimentScore: number
    disciplineScore: number
  }[]
  aiJournalAnalysisEnabled: boolean
  aiSuggestionAdoption: {
    suggestedEmotionTag: {
      suggestions: number
      confirmations: number
      confirmationRate: number
    }
    suggestedRuleBreak: {
      suggestions: number
      confirmations: number
      confirmationRate: number
    }
  }
  traderProfile: {
    label: string
    riskLevel: 'low' | 'medium' | 'high'
    summary: string
    disciplineScore: number
    emotionalityScore: number
    consistencyScore: number
    planAdherenceRate: number
    negativeEmotionRate: number
    ruleBreakRate: number
    aiNegativeSentimentRate: number
    aiDisciplineSignal: number
  } | null
  meta: MetaType
}

export type WinLossAmountType = {
  period: string
  win: {
    count: number
    money: number
  }
  loss: {
    count: number
    money: number
  }
}

export type TradeDistributionType = {
  time: string
  amount: number
}

export type TimeReportType = {
  winLossAmount: WinLossAmountType[]
  tradeDistribution: TradeDistributionType[]
  meta: MetaType
}

export type DurationItemType = {
  amount: number
  duration: string
}

export type StressLevelType = {
  positive: number
  negative: number
  duration: string
}

export type DurationReportType = {
  moneyEquivalence: DurationItemType[]
  numberOfTransactions: DurationItemType[]
  stressLevel: StressLevelType[]
  meta: MetaType
}

export type RiskReportItemType = {
  negativeLabel: string
  positiveLabel: string
  perCent: number
  question: string
}

export type RiskReportType = {
  data: RiskReportItemType[]
  meta: MetaType
}

export type ReportsInitialStateType = {
  time: TimeReportType
  duration: DurationReportType
  psychology: PsychologyType
  risk: RiskReportType
  strategy: StrategiesType
  tradingConfluence: TradingConfluencesType
  instruments: InstrumentsType
  drawdown: DrawdownType
  reportsForPrint: ReportsEnum[]
}

export type StrategiesType = {
  mostPopularProfitable: { strategy: string; amount: number; profit: number }[]
  topStrategyByLevelOfStress: { strategy: string; perCent: number }[]
  topBySystemQualityNumber: {
    symbol: string
    amount: number
    sqn: number | null
  }[]
  meta: MetaType
}

export type TradingConfluencesType = {
  mostPopularProfitable: { name: string; amount: number; profit: number }[]
  topByLevelOfStress: { name: string; perCent: number }[]
  topBySystemQualityNumber: {
    symbol: string
    amount: number
    sqn: number | null
  }[]
  meta: MetaType
}

export type InstrumentsType = {
  topByMoneyNumber: { symbol: string; amount: number; money: number }[]
  topByProfitLoss: { symbol: string; profit: number; loss: number }[]
  topByLevelOfStress: { symbol: string; perCent: number }[]
  meta: MetaType
}

export type SqnWidgetType = {
  symbol: string
  amount: number
  sqn: number | null
}

export type DrawdownItemType = { period: string; value: number }

export type DrawdownType = {
  averageDrawdown: DrawdownItemType[]
  biggestDrawdown: DrawdownItemType[]
  averageNumberOfTrades: DrawdownItemType[]
  averageNumberOfDays: DrawdownItemType[]
  numberOfDays: DrawdownItemType[]
  meta: MetaType
}

export enum DrawdownChartTypeEnum {
  linear = 'linear',
  bar = 'bar',
}

export type DrawdownChartProps = {
  data: DrawdownItemType[]
  displayWidth: number
  chartLabel: string
  chartHeaderText: string
  chartHeaderExplanation: string
  chartType: DrawdownChartTypeEnum
  currency?: string
}

export type ChartOptions = {
  currency?: string
  showPercentage?: boolean
  isSqn?: boolean
  customTooltipLabelData?: {
    data: { value: number | null; amount: number }[]
    title?: string
  }
  displayLegend?: boolean
  disableLegendClick?: boolean
}
